import { useCallback, useEffect } from "react";
import { useState } from "react";
import { Column, createRecord, insertRecordBySeq, ResultTable, ScanRecord } from "./ResultTable";
import { useScanQueueConsumer } from "./useScanQueueConsumer";
import { parseException, showError, showRepeatedScanError, parseExceptionMessageKey } from "utils/exceptionUtils";
import { ScanInput } from "./ScanInput";
import { isTrackingNumber, isValidBarCode, showBarCodeError } from "../../../utils/BarCodeUtil";
import { playScanFailedAoSound } from "../../../utils/scanUtils";
import { t } from '../../../i18n';

export interface HandleScanEventResult {
    success: boolean;
}

type ArrivalScanComponentProps = {
    value1Placeholder: string;
    value1ColumnTitle: string;
    handleScanEvent: (barCode: string) => Promise<HandleScanEventResult>;
    /**
     * 可选，扩展成功记录的额外字段（如 [{ title: 'Type', key: 'type' }]）
     */
    successExtraFields?: { title: string; key: string }[];
    /**
     * 是否开启同时称重模式
     */
    weighSimultaneously?: boolean;
    /**
     * 当前重量数据
     */
    currentWeight?: {weight: number, unit: string} | null;
    /**
     * 蓝牙连接状态
     */
    isBluetoothConnected?: boolean;
}

const getMaxConcurrency = (): number => {
    const DEFAULT = 5;
    const MIN = 1;
    const MAX = 6;
    try {
        const raw = localStorage.getItem('pda.pickupArrival.maxConcurrency');
        if (!raw) return DEFAULT;
        const parsed = Number.parseInt(raw, 10);
        if (Number.isNaN(parsed)) return DEFAULT;
        return Math.min(MAX, Math.max(MIN, parsed));
    } catch (_) {
        return DEFAULT;
    }
};

export const ArrivalRelatedScanComponent: React.FC<ArrivalScanComponentProps> = ({
    value1Placeholder,
    value1ColumnTitle,
    handleScanEvent,
    successExtraFields,
    weighSimultaneously,
    currentWeight,
    isBluetoothConnected
}) => {
    const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
    const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);
    const [value1, setValue1] = useState('');

    // 当同时称重开关变化时，清空已扫描列表
    useEffect(() => {
        setSuccessRecords([]);
        setFailedRecords([]);
    }, [weighSimultaneously]);

    const onProcess = useCallback(async (event: { id: string; seq: number; code: string; ts: number }) => {
        const { code: barCode, seq: eventSeq } = event;

        // 兼容收起键盘
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }

        setValue1(barCode);

        try {
            const result = await handleScanEvent(barCode);
            if (result.success) {
                // 动态合并额外字段
                const extra: Record<string, any> = {};
                if (successExtraFields) {
                    const resultAny = result as any;
                    successExtraFields.forEach(f => {
                        if (resultAny[f.key] !== undefined) extra[f.key] = resultAny[f.key];
                    });
                }
                // 如果开启了同时称重，添加重量信息
                if (weighSimultaneously && currentWeight) {
                    extra.weight = `${currentWeight.weight} ${currentWeight.unit}`;
                }
                const record = createRecord({ value1: barCode, seq: eventSeq, ...extra });
                setSuccessRecords(prev => insertRecordBySeq(prev, record));
            }
        } catch (error) {
            const messageKey = parseExceptionMessageKey(error);
            let message: string;
            if (messageKey === "errors.business.alreadyScanned") {
                showRepeatedScanError(error);
                const { message: errorMessage } = parseException(error);
                message = errorMessage;
            } else {
                const {title, message: errorMessage} = parseException(error);
                playScanFailedAoSound();
                showError({title, message: errorMessage});
                message = errorMessage;
            }
            const record = createRecord({value1: barCode, failedReason: message, seq: eventSeq});
            setFailedRecords(prev => insertRecordBySeq(prev, record));
        }
    }, [handleScanEvent, successExtraFields, weighSimultaneously, currentWeight]);

    const { enqueueScan } = useScanQueueConsumer({
        maxConcurrency: getMaxConcurrency(),
        onProcess,
    });

    // 动态拼接成功列，根据是否同时称重调整顺序
    const successColumns: Column[] = weighSimultaneously
        ? [
            { title: value1ColumnTitle, key: 'value1' },
            ...(successExtraFields || []),
            { title: t('pda.common.time'), key: 'time' }
        ]
        : [
            { title: value1ColumnTitle, key: 'value1' },
            { title: t('pda.common.time'), key: 'time' },
            ...(successExtraFields || [])
        ];

    const failedColumns = [
        { title: value1ColumnTitle, key: 'value1' },
        { title: t('pda.common.time'), key: 'time' },
        {title: t('pda.common.failedReason'), key: 'failedReason'}
    ];

    return (
        <>
            {/* 同时称重标签和蓝牙状态提示 */}
            {weighSimultaneously ? (
                <div className="bg-gray-50 px-4 py-2 rounded-lg mb-2">
                    <div className="text-sm text-green-600 mb-1 text-center">{t('pda.pages.pickUpArrival.weighSimultaneously')}</div>
                    {!isBluetoothConnected && (
                        <div className="flex items-center justify-center text-orange-500 text-sm">
                            <span className="mr-1">⚠️</span>
                            <span>{t('pda.pages.pickUpArrival.bluetoothNotConnected')}</span>
                        </div>
                    )}
                </div>
            ) : (
                <div className="bg-gray-50 px-4 py-2 rounded-lg mb-2">
                    <div className="text-sm text-green-600 text-center">{t('pda.pages.pickUpArrival.notWeighing')}</div>
                </div>
            )}

            <ScanInput
                placeholder={value1Placeholder}
                value={value1}
                onScan={(code) => {
                    if (isValidBarCode(code)) {
                        enqueueScan(code);
                    } else {
                        showBarCodeError(code);
                    }
                }}
            />

            <ResultTable
                successRecords={successRecords}
                failedRecords={failedRecords}
                successColumns={successColumns}
                failedColumns={failedColumns}
            />
        </>
    );
};
